import { useEffect } from 'react';
import { useClerkInstanceAs } from '@/hooks/useClerkGate';
import { registerOrphanedSessionHandler } from '@/lib/orphaned-session';
import { logWarning } from '@/lib/logger';

// Structural view of the Clerk instance (same approach as UserLanguageSync):
// read through the stable instance, not reactive hooks.
interface ClerkInstanceLike {
  signOut: (options?: { redirectUrl?: string }) => Promise<unknown>;
}

/**
 * Signs out a Clerk session whose Aelo user row no longer exists (account
 * deleted server-side). Without this an open tab keeps polling with a live
 * Clerk JWT and every request 401s with `code: 'user_not_found'` — forever.
 *
 * Mounted as an isolated null-rendering leaf next to UserLanguageSync in
 * main.tsx — only under ClerkProvider, never in embedded (Bitrix24) mode.
 * The fetch layer triggers it via lib/orphaned-session.
 */
export function OrphanedSessionSignOut() {
  const clerk = useClerkInstanceAs<ClerkInstanceLike>();

  useEffect(
    () =>
      registerOrphanedSessionHandler(() => {
        logWarning('Signing out orphaned session (user_not_found)', {
          module: 'OrphanedSessionSignOut',
        });
        void clerk.signOut({ redirectUrl: '/sign-in' });
      }),
    [clerk]
  );

  return null;
}
